// src/pages/BulletinsPage.tsx
// One-click bulletin generator - select church, then service

import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar, Church, Clock, ChevronRight } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import * as bulletinService from '../services/bulletinService';
import './BulletinsPage.css';

interface ChurchOption {
  id: number;
  name: string;
}

const BulletinsPage: React.FC = () => {
  const [churches, setChurches] = useState<ChurchOption[]>([]);
  const [selectedChurch, setSelectedChurch] = useState<number | null>(null);
  const [services, setServices] = useState<bulletinService.UpcomingService[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingServices, setLoadingServices] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<number | null>(null);

  // Load churches on mount
  useEffect(() => {
    fetchChurches();
  }, []);

  // Load services when church is selected
  useEffect(() => {
    if (selectedChurch) {
      fetchServices(selectedChurch);
    }
  }, [selectedChurch]);

  const fetchChurches = async () => {
    try {
      setLoading(true);
      // Get all services to extract unique churches
      const data = await bulletinService.getUpcomingServices({ limit: 200 });
      const uniqueChurches = new Map<number, string>();
      data.forEach(s => {
        // Extract church_id from service if available, or use name as key
        if (s.church_name && !uniqueChurches.has(s.service_id)) {
          // We need to get church IDs - for now use the name
          uniqueChurches.set(s.service_id, s.church_name);
        }
      });

      // Get unique church names and create options
      const churchNames = Array.from(new Set(data.map(s => s.church_name))).filter(Boolean);
      const churchOptions: ChurchOption[] = churchNames.map((name, idx) => ({
        id: idx + 1, // Placeholder - we'll filter by name
        name: name
      }));

      setChurches(churchOptions);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch churches');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async (churchIdx: number) => {
    try {
      setLoadingServices(true);
      const churchName = churches[churchIdx - 1]?.name;
      const allData = await bulletinService.getUpcomingServices({ limit: 100 });
      // Filter by church name
      const filtered = allData.filter(s => s.church_name === churchName);
      setServices(filtered);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch services');
    } finally {
      setLoadingServices(false);
    }
  };

  const handleGenerate = async (serviceId: number, variant: '8am' | '10am') => {
    setGeneratingId(serviceId);
    setError(null);
    try {
      const blob = await bulletinService.generateIdealBulletin(serviceId, { variant });

      const service = services.find(s => s.service_id === serviceId);
      const dateStr = service?.date
        ? new Date(service.date).toISOString().slice(0, 10).replace(/-/g, '')
        : String(serviceId);
      const liturgyName = service?.name?.replace(/[^a-z0-9]/gi, '-') || 'bulletin';
      const filename = `${dateStr}-${liturgyName}-${variant}.pdf`;

      bulletinService.downloadPdf(blob, filename);
    } catch (err: any) {
      setError(err.message || 'Failed to generate bulletin');
    } finally {
      setGeneratingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSeasonColor = (season: string): string => {
    const seasonLower = season?.toLowerCase() || '';
    if (seasonLower.includes('advent')) return 'primary';
    if (seasonLower.includes('christmas')) return 'warning';
    if (seasonLower.includes('epiphany')) return 'success';
    if (seasonLower.includes('lent')) return 'secondary';
    if (seasonLower.includes('easter')) return 'warning';
    if (seasonLower.includes('pentecost')) return 'danger';
    return 'secondary';
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container page-below-nav">
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container-fluid page-below-nav px-4">
        <div className="d-flex align-items-center mb-4">
          <FileText className="me-2" size={32} />
          <h1>Bulletin Generator</h1>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        {/* Step 1: Choose Church */}
        {!selectedChurch && (
          <div>
            <h4 className="mb-3">
              <span className="badge bg-primary me-2">1</span>
              Select a Church
            </h4>
            <div className="row">
              {churches.map((church, idx) => (
                <div key={church.name} className="col-md-4 mb-3">
                  <button
                    className="btn btn-outline-primary w-100 py-4 d-flex align-items-center justify-content-between"
                    onClick={() => setSelectedChurch(idx + 1)}
                  >
                    <span className="d-flex align-items-center">
                      <Church size={24} className="me-3" />
                      <span className="fs-5">{church.name}</span>
                    </span>
                    <ChevronRight size={20} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Choose Service */}
        {selectedChurch && (
          <div>
            <div className="d-flex align-items-center mb-3">
              <button
                className="btn btn-link text-decoration-none p-0 me-3"
                onClick={() => {
                  setSelectedChurch(null);
                  setServices([]);
                }}
              >
                &larr; Back
              </button>
              <h4 className="mb-0">
                <span className="badge bg-primary me-2">2</span>
                Select a Service
                <span className="text-muted ms-2 fs-6">({churches[selectedChurch - 1]?.name})</span>
              </h4>
            </div>

            {loadingServices ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : services.length === 0 ? (
              <div className="text-center py-5">
                <Calendar size={64} className="text-muted mb-3" />
                <h3 className="text-muted">No Upcoming Services</h3>
                <p className="text-muted">No services found for this church.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Service</th>
                      <th>Time</th>
                      <th>Season</th>
                      <th className="text-end">Generate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map(service => (
                      <tr key={service.service_id}>
                        <td>
                          <Calendar size={16} className="me-2 text-muted" />
                          {formatDate(service.date)}
                        </td>
                        <td>
                          <strong>{service.name}</strong>
                          {service.special_service_name && (
                            <div className="text-muted small">{service.special_service_name}</div>
                          )}
                        </td>
                        <td>
                          <Clock size={16} className="me-2 text-muted" />
                          {service.time}
                        </td>
                        <td>
                          <span className={`badge bg-${getSeasonColor(service.season)}`}>
                            {service.season}
                          </span>
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-primary"
                              onClick={() => handleGenerate(service.service_id, '10am')}
                              disabled={generatingId === service.service_id}
                              title="Generate 10am bulletin (with music)"
                            >
                              {generatingId === service.service_id ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <>
                                  <Download size={16} className="me-1" />
                                  10am
                                </>
                              )}
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={() => handleGenerate(service.service_id, '8am')}
                              disabled={generatingId === service.service_id}
                              title="Generate 8am bulletin (spoken)"
                            >
                              8am
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default BulletinsPage;
